import { SyntheticEvent, useCallback, useEffect, useRef } from 'react';

const DOUBLE_CLICK_DELAY_MS = 250;

interface UseSingleDoubleClickOptions {
  onSingleClick: (e: SyntheticEvent) => void;
  onDoubleClick: (e: SyntheticEvent) => void;
}

/**
 * Disambiguates single click from double click on the same element.
 * Single click fires after a short delay; double click cancels it
 * and fires immediately.
 */
export function useSingleDoubleClick({
  onSingleClick,
  onDoubleClick,
}: UseSingleDoubleClickOptions): (e: SyntheticEvent) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(
    (e: SyntheticEvent) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        onDoubleClick(e);
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onSingleClick(e);
      }, DOUBLE_CLICK_DELAY_MS);
    },
    [onSingleClick, onDoubleClick]
  );
}
